// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// VIEW — "What was agreed".
//
// A determination settles what the law recognises. Almost everything that
// happens on the ground afterwards is negotiated instead: 1,539 Indigenous Land
// Use Agreements sit on the register against 683 determinations. This view asks
// three things of them — what subject matter the register files them under,
// what the register says they do in law, and how much of that it does not
// record at all.
//
// THE THIRD STATE IS THE POINT. The five statutory flags are blank on 353-369
// of the 1,539 rows. A blank is not a "no": coercing it would report that 578
// agreements do not involve a right to negotiate when the register only says so
// about 224. So every proportion here is drawn in THREE segments — recorded
// yes, recorded no, and not recorded — the third carries the UNRECORDED hatch,
// it is a real filter rather than a footnote, and every percentage prints the
// denominator it was taken over.
//
// TWO NOTES ON FORM.
//
//   * The icicle is built from real <button> elements in HTML rather than SVG
//     rects. It is a rectangular partition, which CSS lays out exactly, and
//     thirty-four bands that must each be clickable, focusable and labelled are
//     far safer as buttons than as SVG nodes wearing role="button". The shape
//     on screen is the same icicle either way.
//
//   * Bands too narrow to click are NOT drawn at a fudged minimum width, which
//     would silently distort the proportion the whole chart exists to show.
//     They are gathered into one band whose width is exactly the sum of its
//     members and which opens into its own row, scaled to its own total.
//     Nothing here is ever drawn wider than its count earned.

import { subjectColour, mixHex, UNRECORDED } from '../theme';
import { chart, el, measure, ticks } from '../components/svg';
import { esc, num, km2, pct, longDate, shortDate, yearOf, tip, tipAttr, plural } from '../format';
import { glossTerm, glossIcon } from '../components/glossary';
import { openOverlay, closeButton } from '../overlay';
import { currentRoute, setParams } from '../router';
import { attachSvgZoom, type SvgZoomHandle } from '../utils/svgZoom';
import { getMeta, loadAgreements } from '../data';
import type { Agreement, AgreementsFile } from '../types';

/* ─────────────────────────────── constants ─────────────────────────────── */

const FLAG_KEYS = [
  'rightToNegotiate', 'surrender', 'consentToFutureAct', 'validatesFutureAct', 'intermediatePeriodActs',
] as const;
type FlagKey = (typeof FLAG_KEYS)[number];
type FlagState = 'yes' | 'no' | 'unrecorded';

/**
 * The register's own fields, named by their source column so a reader can go
 * back to the data and check. Each sentence describes what the FIELD records —
 * not what the agreement legally achieves. The agreement is the document.
 */
const FLAG_META: Record<FlagKey, { label: string; column: string; plain: string; gloss: string }> = {
  rightToNegotiate: {
    label: 'Right to negotiate',
    column: 'RTN',
    plain: 'The register records this agreement as dealing with the right to negotiate — the procedure that applies before certain acts, typically a mining tenement, can be done over the area.',
    gloss: 'right-to-negotiate',
  },
  surrender: {
    label: 'Surrender of native title',
    column: 'SURRAREAS',
    plain: 'The register records this agreement as including the surrender of native title over some or all of the agreement area. Surrender is one of the ways native title comes to an end.',
    gloss: 'extinguishment',
  },
  consentToFutureAct: {
    label: 'Consent to a future act',
    column: 'CONSENTFA',
    plain: 'The register records this agreement as consenting to a future act — an act done after 1 January 1994 that affects native title.',
    gloss: 'future-act',
  },
  validatesFutureAct: {
    label: 'Validates a future act',
    column: 'VALIDATEFA',
    plain: 'The register records this agreement as validating a future act, so that an act which might otherwise have been invalid stands.',
    gloss: 'future-act',
  },
  intermediatePeriodActs: {
    label: 'Intermediate period acts',
    column: 'INTERMACTS',
    plain: 'The register records this agreement as dealing with intermediate period acts — acts done between 1 January 1994 and 23 December 1996, the period the 1998 amendments were written around.',
    gloss: 'intermediate-period-act',
  },
};

const FLAG_STATE_LABEL: Record<FlagState, string> = {
  yes: 'recorded: yes',
  no: 'recorded: no',
  unrecorded: 'not recorded',
};
const FLAG_STATES: FlagState[] = ['yes', 'no', 'unrecorded'];

const JURIS_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const JURIS_NAME: Record<string, string> = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia',
  WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'Australian Capital Territory',
};

/** The authored pixel width of the icicle. Band widths are reasoned about
 *  against it, and the row scrolls sideways inside its own scroller below it. */
const ICICLE_PX = 860;
/** Narrower than this and a band joins the gathered band instead of shrinking. */
const MIN_BAND_PX = 9;
const LIST_CAP = 120;

/** Sentinel for "filed under this subject with no sub-tag at all". */
const NOSUB = '__none__';
const KEY_BUNDLE = '__bundle__';
const KEY_SUBBUNDLE = '__subbundle__';

/** A neutral ink for the ribbon: presence, absence, unknown — never a valence,
 *  and deliberately none of the four outcome hues. */
const INK = '#57514A';
const INK_PALE = mixHex(INK, '#ffffff', 0.76);
/** The third state's paint, built from the theme constant so it cannot drift. */
const UNREC_HATCH = `repeating-linear-gradient(45deg, ${UNRECORDED} 0 2px, #efeae0 2px 6px)`;

/* ──────────────────────────────── helpers ──────────────────────────────── */

function flagStateOf(a: Agreement, k: FlagKey): FlagState {
  const v = a[k];
  return v == null ? 'unrecorded' : v ? 'yes' : 'no';
}

/** Dark or light text, whichever survives on this fill. */
function readable(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const srgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  return lum > 0.42 ? '#1a1815' : '#ffffff';
}

type BandAct = 'top' | 'bundle' | 'sub' | 'nosub' | 'subbundle';

interface Band {
  act: BandAct;
  key: string;
  label: string;
  count: number;
  colour: string;
  hatch?: boolean;
  note?: string;
}

interface ViewState {
  top: string | null;
  sub: string | null;
  noSub: boolean;
  bundle: boolean;
  subBundle: boolean;
  juris: string | null;
  flag: { key: FlagKey; state: FlagState } | null;
  showAll: boolean;
}

/* ──────────────────────────────── the view ─────────────────────────────── */

export async function renderAgreements(host: HTMLElement): Promise<void> {
  const file: AgreementsFile = await loadAgreements();
  const meta = getMeta();
  const rows = file.rows;
  const TOTAL = rows.length;

  const extracted = meta.sources.find((s) => s.key === 'agreements')?.extracted ?? null;
  const lastYear = file.perYear.length ? file.perYear[file.perYear.length - 1].year : null;

  /* Colour comes from the subject family and nowhere else — never an outcome
     hue. Thirty-four tags against twelve colours, so the second and third dozen
     are taken lighter and darker; the label, not the hue, identifies a band. */
  const ORDER = file.subjects.map((s) => s.top);
  const COLOUR = new Map<string, string>();
  file.subjects.forEach((s, i) => {
    const base = subjectColour(i);
    const tier = Math.floor(i / 12);
    COLOUR.set(s.top, tier === 0 ? base : tier === 1 ? mixHex(base, '#ffffff', 0.32) : mixHex(base, '#1a1815', 0.3));
  });
  const colourOf = (name: string, parent?: string): string =>
    COLOUR.get(name) ?? (parent ? mixHex(parent, '#ffffff', 0.42) : INK);

  const jurisCounts = new Map<string, number>();
  for (const a of rows) jurisCounts.set(a.juris, (jurisCounts.get(a.juris) ?? 0) + 1);

  const untaggedAll = rows.filter((a) => !a.subjects.length).length;
  const noSubAll = rows.filter((a) => a.subjects.some((s) => !s.subs.length)).length;
  const bandSum = file.subjects.reduce((t, s) => t + s.count, 0);
  const qld = jurisCounts.get('QLD') ?? 0;
  /* Every area on this site is land only, and `agreementNaiveSumKm2` in meta is
     the sum over the land agreements alone. Summing all 1,539 rows instead
     gives 5,720,415 km² and would not reconcile with the union it is being
     compared against, so the sea agreements come out here too. */
  const landRows = rows.filter((a) => !a.sea);
  const landCount = landRows.length;

  /* ───────────────────────────── state ───────────────────────────── */

  const state: ViewState = {
    top: null, sub: null, noSub: false, bundle: false, subBundle: false,
    juris: null, flag: null, showAll: false,
  };
  {
    const params = currentRoute().params;
    const pSubj = params.get('subj');
    if (pSubj && ORDER.includes(pSubj)) state.top = pSubj;
    const pSub = params.get('sub');
    if (state.top && pSub === NOSUB) state.noSub = true;
    else if (state.top && pSub) state.sub = pSub;
    const pJuris = params.get('juris');
    if (pJuris && JURIS_ORDER.includes(pJuris)) state.juris = pJuris;
    const fk = params.get('flag');
    const fv = params.get('fv');
    if (fk && (FLAG_KEYS as readonly string[]).includes(fk) && fv && (FLAG_STATES as string[]).includes(fv)) {
      state.flag = { key: fk as FlagKey, state: fv as FlagState };
    }
  }

  /* ─────────────────────────── static shell ──────────────────────── */

  host.innerHTML = `
    <div class="view-head">
      <h1>What was agreed</h1>
      <p class="lede">A determination settles what the law recognises. Almost everything that happens next is
        negotiated: ${esc(num(TOTAL))} ${glossTerm('ilua', 'Indigenous Land Use Agreements')} sit on the register
        against ${esc(num(meta.counts.determinations))} determinations. This is what the register files them
        under, what it records them as doing in law — and how much of that it does not record at all.</p>
    </div>

    <div class="stat-row" style="margin-bottom: var(--space-lg)">
      <div class="stat" data-tip="${tipAttr(`${num(TOTAL)} agreements on the register`, `${num(file.byType['Area Agreement'] ?? 0)} area agreements, ${num(file.byType['Body Corporate'] ?? 0)} body corporate agreements`, `${num(file.certified)} are recorded as certified by a representative body`)}">
        <div class="stat-value">${esc(num(TOTAL))}</div>
        <div class="stat-label">Agreements on the register</div>
        <div class="stat-note">${esc(num(file.byType['Area Agreement'] ?? 0))} area · ${esc(num(file.byType['Body Corporate'] ?? 0))} body corporate</div>
      </div>
      <div class="stat" data-tip="${tipAttr(`Queensland: ${num(qld)} of ${num(TOTAL)} agreements (${pct((qld / TOTAL) * 100)})`, 'The register does not record why the distribution is like this.')}">
        <div class="stat-value">${esc(pct((qld / TOTAL) * 100))}</div>
        <div class="stat-label">Are in Queensland</div>
        <div class="stat-note">${esc(num(qld))} of ${esc(num(TOTAL))} agreements</div>
      </div>
      <div class="stat" data-tip="${tipAttr('The five statutory flags are blank on between 353 and 369 rows, depending on the field.', `That is ${pct((353 / TOTAL) * 100)} to ${pct((369 / TOTAL) * 100)} of the ${num(TOTAL)} agreements.`, 'A blank is never counted as a no on this site.')}">
        <div class="stat-value">353–369</div>
        <div class="stat-label">Rows leave each flag blank</div>
        <div class="stat-note">about ${esc(pct((361 / TOTAL) * 100, 0))} of the register</div>
      </div>
      <div class="stat" data-tip="${tipAttr(`The register's own area column sums to ${km2(meta.areas.agreementNaiveSumKm2, { exact: true })}.`, `The ground actually covered, measured once, is ${km2(meta.areas.agreementUnionKm2, { exact: true })}.`, `${pct(meta.areas.agreementOverlapPct)} of the sum is the same ground counted twice or more.`)}">
        <div class="stat-value">${esc(pct(meta.areas.agreementOverlapPct))}</div>
        <div class="stat-label">Of the area column is double-counted</div>
        <div class="stat-note">a sum is not an area</div>
      </div>
    </div>

    <div class="stack" style="margin-bottom: var(--space-lg)">
      <div class="note" id="agr-note-vocab"></div>
      <div class="note" id="agr-note-qld"></div>
      <div class="note warn" id="agr-note-area"></div>
    </div>

    <div class="chips" id="agr-juris" aria-label="Filter by jurisdiction"></div>
    <div class="chips" id="agr-chips" aria-label="Active filters"></div>

    <div class="grid-2" style="margin-bottom: var(--space-lg)">
      <div class="card">
        <div class="card-title">The subject vocabulary</div>
        <div class="card-sub">Band width is that subject's share of the agreements currently in view. Choose a
          band to open its sub-tags and recompute everything else on the page.</div>
        <div class="agr-scroll" id="agr-icicle-wrap" style="margin-top: var(--space-md)">
          <div class="agr-icicle">
            <div class="agr-row" id="agr-row-top"></div>
            <div class="agr-rowhead" id="agr-sub-head"></div>
            <div class="agr-row" id="agr-row-sub"></div>
          </div>
        </div>
        <p class="xsmall muted" id="agr-icicle-foot" style="margin-top: var(--space-sm)"></p>
      </div>

      <div class="card">
        <div class="card-title">What the register records them as doing in law</div>
        <div class="card-sub" id="agr-ribbon-sub"></div>
        <div class="stack" id="agr-ribbon" style="margin-top: var(--space-md)"></div>
        <div class="legend" style="margin-top: var(--space-md)">
          <span class="legend-item"><span class="legend-swatch" style="background:${esc(INK)}"></span>Recorded: yes</span>
          <span class="legend-item"><span class="legend-swatch" style="background:${esc(INK_PALE)}"></span>Recorded: no</span>
          <span class="legend-item"><span class="legend-swatch agr-sw-unrec"></span>Not recorded — blank in the register</span>
        </div>
        <div class="note xsmall" style="margin-top: var(--space-md)">These are the register's own yes/no fields
          (RTN, SURRAREAS, CONSENTFA, VALIDATEFA, INTERMACTS). A blank is <strong>not</strong> a no, and it is
          never counted as one here. Dark means the field is recorded yes, pale means recorded no, hatched means
          the register is silent — presence, absence and silence, not good and bad. Reading the agreement itself
          is the only way to know what it actually does.</div>
      </div>
    </div>

    <div class="card" style="margin-bottom: var(--space-lg)">
      <div class="card-title">Agreements entered on the register each year</div>
      <div class="card-sub" id="agr-year-sub"></div>
      <div class="chart-frame" id="agr-year" style="margin-top: var(--space-md)"></div>
      <p class="xsmall muted" id="agr-year-foot" style="margin-top: var(--space-sm)"></p>
    </div>

    <div class="card">
      <div class="row" style="margin-bottom: var(--space-md)">
        <div class="grow">
          <div class="card-title">The agreements themselves</div>
          <div class="card-sub" id="agr-list-sub"></div>
        </div>
        <button type="button" class="btn btn-sm shrink0" id="agr-showall" hidden>List all</button>
      </div>
      <div id="agr-list"></div>
      <p class="xsmall muted" style="margin-top: var(--space-sm)">Subject matter is reproduced exactly as the
        register's SUBJMATTER field states it. Choose an agreement's name for its full record.</p>
    </div>
  `;

  /* The three notes this view is required to carry, written from the numbers
     actually in the file rather than from memory — including where that
     contradicts the received description of the data. */
  host.querySelector('#agr-note-vocab')!.innerHTML = `
    <strong>The bands are index terms, not a description of the agreements.</strong>
    The register's SUBJMATTER field can carry more than one top-level subject for a single agreement, and where
    it does, that agreement is counted under each of them — so a row of bands is not guaranteed to sum to the
    number of agreements. In this extract every one of the ${esc(num(TOTAL))} agreements happens to carry
    exactly one top-level subject and ${esc(num(untaggedAll))} are untagged, so the top row does sum to
    ${esc(num(bandSum))}. The second row genuinely does not: an agreement can carry several sub-tags at once,
    and ${esc(num(noSubAll))} agreements carry none at all, so sub-tags are counted per tag and scaled to their
    own total rather than to the parent band. None of this describes what an agreement contains.`;

  host.querySelector('#agr-note-qld')!.innerHTML = `
    <strong>Queensland holds ${esc(num(qld))} of the ${esc(num(TOTAL))} agreements</strong> —
    ${esc(pct((qld / TOTAL) * 100))} of the register, against ${esc(num(jurisCounts.get('WA') ?? 0))} in Western
    Australia and ${esc(num(jurisCounts.get('NT') ?? 0))} in the Northern Territory. That reflects Queensland
    land-administration practice, not the density of Aboriginal interests — and the register does not tell us
    why. Tasmania and the Australian Capital Territory have no registered agreements at all: a real zero, not
    missing data, and you can choose either below to see it stated.`;

  host.querySelector('#agr-note-area')!.innerHTML = `
    <strong>A sum is not an area.</strong> Add up this register's own area column across the
    ${esc(num(landCount))} agreements that cover land — sea agreements are excluded here as they are everywhere
    on this site — and it comes to ${esc(km2(meta.areas.agreementNaiveSumKm2, { exact: true }))}.
    The ground those agreements actually cover, measured once as a
    ${glossTerm('dissolved-union', 'dissolved union')} in an
    ${glossTerm('equal-area-projection', 'equal-area projection')}, is
    ${esc(km2(meta.areas.agreementUnionKm2, { exact: true }))}.
    ${esc(pct(meta.areas.agreementOverlapPct))} of the sum is the same ground counted twice or more, because
    agreements overlap one another and often sit on the same country. Every area total on this page is a sum
    and is labelled as one; only that union figure is an area.`;

  /* ───────────────────────── element handles ─────────────────────── */

  const elWrap = host.querySelector<HTMLElement>('#agr-icicle-wrap')!;
  const elJuris = host.querySelector<HTMLElement>('#agr-juris')!;
  const elChips = host.querySelector<HTMLElement>('#agr-chips')!;
  const elRowTop = host.querySelector<HTMLElement>('#agr-row-top')!;
  const elSubHead = host.querySelector<HTMLElement>('#agr-sub-head')!;
  const elRowSub = host.querySelector<HTMLElement>('#agr-row-sub')!;
  const elIcicleFoot = host.querySelector<HTMLElement>('#agr-icicle-foot')!;
  const elRibbon = host.querySelector<HTMLElement>('#agr-ribbon')!;
  const elRibbonSub = host.querySelector<HTMLElement>('#agr-ribbon-sub')!;
  const elYear = host.querySelector<HTMLElement>('#agr-year')!;
  const elYearSub = host.querySelector<HTMLElement>('#agr-year-sub')!;
  const elYearFoot = host.querySelector<HTMLElement>('#agr-year-foot')!;
  const elList = host.querySelector<HTMLElement>('#agr-list')!;
  const elListSub = host.querySelector<HTMLElement>('#agr-list-sub')!;
  const elShowAll = host.querySelector<HTMLButtonElement>('#agr-showall')!;

  /* Bands are addressed by index, so a subject name containing a quote or a
     slash can never break an attribute. */
  let topBands: Band[] = [];
  let subBands: Band[] = [];
  let listed: Agreement[] = [];
  let zoom: SvgZoomHandle | null = null;

  /* The year chart is drawn at its container's REAL pixel width.
     Letting the SVG stretch to `width: 100%` instead scales every label with
     the box — 11px type rendered at 4.7px on a 375px phone.
     `render` receives a DETACHED host, so the first pass has nothing to measure
     and falls back to an authored width. Three things then correct it, because
     no one of them is reliable on its own: a `setTimeout(0)`, which runs after
     the router has put this view in the document and, unlike
     requestAnimationFrame, still runs when the page was opened in a BACKGROUND
     TAB; a ResizeObserver for later column changes; and re-measuring on every
     redraw. `lastW` stops the observer chasing its own output. */
  const chartWidth = () => Math.max(280, measure(elYear, 880));
  let lastW = 0;

  /* ─────────────────────────── selection ─────────────────────────── */

  const inTop = (a: Agreement, top: string) => a.subjects.some((s) => s.top === top);

  function poolBeforeSubject(): Agreement[] {
    return rows.filter((a) => {
      if (state.juris && a.juris !== state.juris) return false;
      if (state.flag && flagStateOf(a, state.flag.key) !== state.flag.state) return false;
      return true;
    });
  }

  function applySubject(pool: Agreement[]): Agreement[] {
    if (!state.top) return pool;
    const top = state.top;
    let out = pool.filter((a) => inTop(a, top));
    if (state.noSub) out = out.filter((a) => a.subjects.some((s) => s.top === top && !s.subs.length));
    else if (state.sub) out = out.filter((a) => a.subjects.some((s) => s.top === top && s.subs.includes(state.sub!)));
    return out;
  }

  function selectionLabel(): string {
    if (!state.top) return 'all agreements in view';
    if (state.noSub) return `${state.top} — no sub-tag recorded`;
    if (state.sub) return `${state.top} → ${state.sub}`;
    return state.top;
  }

  function syncUrl(): void {
    setParams({
      subj: state.top,
      sub: state.noSub ? NOSUB : state.sub,
      juris: state.juris,
      flag: state.flag?.key ?? null,
      fv: state.flag?.state ?? null,
    });
  }

  /* ───────────────────────────── icicle ──────────────────────────── */

  function buildTopBands(pool: Agreement[]): { bands: Band[]; total: number; grouped: Band[] } {
    const counts = new Map<string, number>();
    for (const a of pool) for (const s of a.subjects) counts.set(s.top, (counts.get(s.top) ?? 0) + 1);
    const present = ORDER.filter((t) => (counts.get(t) ?? 0) > 0);
    const total = present.reduce((t, k) => t + (counts.get(k) ?? 0), 0);
    const minCount = total > 0 ? Math.max(1, Math.ceil((MIN_BAND_PX / ICICLE_PX) * total)) : 1;
    const mk = (t: string): Band => ({ act: 'top', key: t, label: t, count: counts.get(t)!, colour: colourOf(t) });

    const big: Band[] = [];
    const grouped: Band[] = [];
    for (const t of present) ((counts.get(t)! >= minCount) ? big : grouped).push(mk(t));
    if (grouped.length < 2) return { bands: present.map(mk), total, grouped: [] };

    big.push({
      act: 'bundle',
      key: KEY_BUNDLE,
      label: `${grouped.length} smaller subjects`,
      count: grouped.reduce((t, b) => t + b.count, 0),
      colour: mixHex(INK, '#ffffff', 0.2),
      note: 'Gathered so each stays wide enough to choose. This band is exactly as wide as its members add up to.',
    });
    return { bands: big, total, grouped };
  }

  function buildSubBands(pool: Agreement[], top: string): {
    bands: Band[]; total: number; grouped: Band[]; tagEntries: number; noSub: number; agreements: number;
  } {
    const inside = pool.filter((a) => inTop(a, top));
    const counts = new Map<string, number>();
    let noSub = 0;
    for (const a of inside) {
      for (const s of a.subjects) {
        if (s.top !== top) continue;
        if (!s.subs.length) { noSub += 1; continue; }
        for (const name of s.subs) counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    const parent = colourOf(top);
    const ordered = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const tagEntries = ordered.reduce((t, [, c]) => t + c, 0);
    const total = tagEntries + noSub;
    const minCount = total > 0 ? Math.max(1, Math.ceil((MIN_BAND_PX / ICICLE_PX) * total)) : 1;

    const big: Band[] = [];
    const grouped: Band[] = [];
    for (const [name, c] of ordered) {
      const band: Band = { act: 'sub', key: name, label: name, count: c, colour: colourOf(name, parent) };
      (c >= minCount ? big : grouped).push(band);
    }
    const bands = grouped.length >= 2 ? [...big] : [...big, ...grouped];
    if (grouped.length >= 2) {
      bands.push({
        act: 'subbundle',
        key: KEY_SUBBUNDLE,
        label: `${grouped.length} smaller sub-tags`,
        count: grouped.reduce((t, b) => t + b.count, 0),
        colour: mixHex(parent, '#ffffff', 0.62),
        note: 'Gathered so each stays wide enough to choose. This band is exactly as wide as its members add up to.',
      });
    }
    if (noSub > 0) {
      bands.push({
        act: 'nosub',
        key: NOSUB,
        label: 'No sub-tag recorded',
        count: noSub,
        colour: UNRECORDED,
        hatch: true,
        note: 'The register files these agreements under the top-level subject and stops there. Not a sub-tag meaning "none" — an absence.',
      });
    }
    return { bands, total, grouped, tagEntries, noSub, agreements: inside.length };
  }

  function bandHtml(bands: Band[], total: number, selectedKey: string | null, denom: string): string {
    if (!bands.length || total <= 0) return '';
    return bands.map((b, i) => {
      const share = (b.count / total) * 100;
      const px = (share / 100) * ICICLE_PX;
      const on = selectedKey === b.key;
      const style = b.hatch
        ? `background:${UNREC_HATCH};color:#1a1815;`
        : `background:${b.colour};color:${readable(b.colour)};`;
      const label = px >= 54 ? `<span class="agr-band-label">${esc(b.label)}</span>` : '';
      const count = px >= 116 ? `<span class="agr-band-count">${esc(num(b.count))}</span>` : '';
      return `<button type="button" class="agr-band${on ? ' is-on' : ''}"
        style="flex:0 0 ${share.toFixed(4)}%;${style}"
        data-band="${i}" aria-pressed="${on}"
        aria-label="${esc(b.label)}, ${esc(plural(b.count, 'agreement'))}, ${esc(pct(share))} of ${esc(denom)}"
        data-tip="${tipAttr(
          b.label,
          `${plural(b.count, 'agreement')} — ${pct(share)} of ${denom}`,
          b.note,
          on ? 'Chosen. Choose again to clear.' : 'Choose to filter this page.',
        )}">${label}${count}</button>`;
    }).join('');
  }

  function drawIcicle(pool: Agreement[]): void {
    const built = buildTopBands(pool);
    topBands = built.bands;
    const shownKeys = new Set(built.bands.map((b) => b.key));
    const selectedTopKey = state.bundle
      ? KEY_BUNDLE
      : state.top
        ? (shownKeys.has(state.top) ? state.top : KEY_BUNDLE)
        : null;
    elRowTop.innerHTML = bandHtml(built.bands, built.total, selectedTopKey, `${num(built.total)} subject entries`)
      || '<p class="xsmall muted" style="padding:var(--space-sm) 0">No agreements match the current filter, so there are no subject bands to draw.</p>';

    if (state.bundle && built.grouped.length) {
      subBands = built.grouped;
      const gTotal = built.grouped.reduce((t, b) => t + b.count, 0);
      elSubHead.innerHTML = `<span class="agr-rowhead-t">The ${esc(num(built.grouped.length))} smaller subjects</span>
        <span class="xsmall muted">${esc(plural(gTotal, 'agreement'))} between them, scaled to their own total so each can be chosen.</span>`;
      elRowSub.innerHTML = bandHtml(subBands, gTotal, state.top, `${num(gTotal)} agreements in this group`);
      elIcicleFoot.textContent = 'The gathered band above is exactly as wide as its members add up to — nothing on this page is drawn wider than its count.';
      return;
    }

    if (state.top) {
      const sb = buildSubBands(pool, state.top);
      const drilled = state.subBundle && sb.grouped.length >= 2;
      subBands = drilled ? sb.grouped : sb.bands;
      const sTotal = drilled ? sb.grouped.reduce((t, b) => t + b.count, 0) : sb.total;
      const selKey = state.noSub ? NOSUB : state.sub;
      elSubHead.innerHTML = `<span class="agr-rowhead-t">Sub-tags within ${esc(state.top)}${drilled ? ` — the ${esc(num(sb.grouped.length))} smallest` : ''}</span>
        <span class="xsmall muted">${drilled
          ? `${esc(plural(sTotal, 'sub-tag entry', 'sub-tag entries'))} between them, scaled to their own total so each can be chosen.`
          : `${esc(num(sb.tagEntries))} sub-tag entries across ${esc(plural(sb.agreements, 'agreement'))}; ${esc(num(sb.noSub))} of those agreements carry no sub-tag.`}</span>
        ${drilled ? `<button type="button" class="btn btn-sm" data-act="subback">← All sub-tags of ${esc(state.top)}</button>` : ''}`;
      elRowSub.innerHTML = sTotal > 0
        ? bandHtml(subBands, sTotal, selKey, `${num(sTotal)} entries in this row`)
        : '<p class="xsmall muted" style="padding:var(--space-sm) 0">No agreement in the current filter carries this subject.</p>';
      elIcicleFoot.textContent = 'Sub-tags are counted per tag, so they add up to more than the parent band — one agreement can carry several. The hatched band is the third state: filed under the subject, with no sub-tag at all.';
      return;
    }

    subBands = [];
    elSubHead.innerHTML = '<span class="agr-rowhead-t">Sub-tags</span>'
      + '<span class="xsmall muted">Choose a subject above to open its sub-tags.</span>';
    elRowSub.innerHTML = '';
    elIcicleFoot.textContent = 'Band width is the subject’s share of the agreements currently in view. Hover any band for its exact count and denominator.';
  }

  /* ───────────────────────────── ribbon ──────────────────────────── */

  function tally(set: Agreement[], k: FlagKey): Record<FlagState, number> {
    const t: Record<FlagState, number> = { yes: 0, no: 0, unrecorded: 0 };
    for (const a of set) t[flagStateOf(a, k)] += 1;
    return t;
  }

  function segStyle(s: FlagState): string {
    if (s === 'unrecorded') return `background:${UNREC_HATCH};color:#1a1815;`;
    return `background:${s === 'yes' ? INK : INK_PALE};color:${s === 'yes' ? '#ffffff' : '#1a1815'};`;
  }

  function liveSeg(k: FlagKey, s: FlagState, count: number, total: number, baseCount: number): string {
    if (count <= 0) return '';
    const share = (count / total) * 100;
    const baseShare = (baseCount / TOTAL) * 100;
    const on = state.flag?.key === k && state.flag.state === s;
    const label = share >= 13 ? pct(share, share < 10 ? 1 : 0) : '';
    return `<button type="button" class="agr-seg${on ? ' is-on' : ''}" style="flex:0 0 ${share.toFixed(4)}%;${segStyle(s)}"
      data-flag="${k}" data-state="${s}" aria-pressed="${on}"
      aria-label="${esc(FLAG_META[k].label)}, ${esc(FLAG_STATE_LABEL[s])}, ${esc(num(count))} of ${esc(num(total))}"
      data-tip="${tipAttr(
        `${FLAG_META[k].label} — ${FLAG_STATE_LABEL[s]}`,
        `${num(count)} of ${num(total)} agreements in the current selection (${pct(share)})`,
        `All ${num(TOTAL)} agreements: ${num(baseCount)} (${pct(baseShare)})`,
        s === 'unrecorded' ? 'Blank in the register. Not a no.' : null,
        on ? 'Chosen. Choose again to clear.' : 'Choose to keep only these agreements.',
      )}">${esc(label)}</button>`;
  }

  function baseSeg(k: FlagKey, s: FlagState, count: number): string {
    if (count <= 0) return '';
    const share = (count / TOTAL) * 100;
    return `<span class="agr-base-seg" style="flex:0 0 ${share.toFixed(4)}%;${segStyle(s)}"
      data-tip="${tipAttr(
        `${FLAG_META[k].label} — ${FLAG_STATE_LABEL[s]}`,
        `Pinned baseline: ${num(count)} of all ${num(TOTAL)} agreements (${pct(share)})`,
        'The baseline is for comparison and does not change with the filters.',
      )}"></span>`;
  }

  function chipHtml(k: FlagKey, s: FlagState, count: number, total: number): string {
    const swatch = s === 'unrecorded'
      ? '<span class="legend-swatch agr-sw-unrec"></span>'
      : `<span class="legend-swatch" style="background:${s === 'yes' ? INK : INK_PALE}"></span>`;
    const text = `${FLAG_STATE_LABEL[s]} ${num(count)}`;
    if (count <= 0) {
      return `<span class="pill" data-tip="${tipAttr(`${FLAG_META[k].label} — ${FLAG_STATE_LABEL[s]}`, `No agreement in the current selection of ${num(total)}.`)}">${swatch}${esc(text)}</span>`;
    }
    const share = (count / total) * 100;
    const on = state.flag?.key === k && state.flag.state === s;
    return `<button type="button" class="pill" data-flag="${k}" data-state="${s}" aria-pressed="${on}"
      data-tip="${tipAttr(
        `${FLAG_META[k].label} — ${FLAG_STATE_LABEL[s]}`,
        `${num(count)} of ${num(total)} in the current selection (${pct(share)})`,
        on ? 'Chosen. Choose again to clear.' : 'Choose to keep only these agreements.',
      )}">${swatch}${esc(text)}</button>`;
  }

  function drawRibbon(set: Agreement[]): void {
    const total = set.length;
    elRibbonSub.innerHTML = total > 0
      ? `Recomputed for <strong>${esc(selectionLabel())}</strong> — ${esc(plural(total, 'agreement'))}. Each bar sits
         above a pinned baseline of all ${esc(num(TOTAL))} agreements.`
      : `Nothing is in the current selection, so there is nothing to recompute. The pinned baseline for all
         ${esc(num(TOTAL))} agreements is still drawn below each bar.`;

    elRibbon.innerHTML = FLAG_KEYS.map((k) => {
      const live = tally(set, k);
      const base = file.flags[k];
      const bar = total > 0
        ? `<div class="agr-bar">${FLAG_STATES.map((s) => liveSeg(k, s, live[s], total, base[s])).join('')}</div>`
        : '<div class="agr-bar agr-bar-empty"><span class="xsmall">No agreements in the current selection</span></div>';
      const delta = total > 0
        ? (() => {
          const d = (live.yes / total) * 100 - (base.yes / TOTAL) * 100;
          return Math.abs(d) < 0.05
            ? 'the same share as the register overall'
            : `${d > 0 ? '+' : '−'}${Math.abs(d).toFixed(1)} points on “yes” vs all ${num(TOTAL)}`;
        })()
        : '';
      return `<div class="agr-flag">
        <div class="row" style="gap:var(--space-xs)">
          <strong class="grow">${esc(FLAG_META[k].label)}${glossIcon(FLAG_META[k].gloss)}</strong>
          <span class="mono xsmall muted shrink0">${esc(FLAG_META[k].column)}</span>
        </div>
        <p class="xsmall muted">${esc(FLAG_META[k].plain)}</p>
        ${bar}
        <div class="agr-baserow">
          <span class="xsmall muted shrink0">All ${esc(num(TOTAL))}</span>
          <div class="agr-base">${FLAG_STATES.map((s) => baseSeg(k, s, base[s])).join('')}</div>
        </div>
        <div class="row xsmall" style="gap:var(--space-xs)">
          ${FLAG_STATES.map((s) => chipHtml(k, s, live[s], total)).join('')}
          <span class="xsmall muted">of ${esc(num(total))}${delta ? ` · ${esc(delta)}` : ''}</span>
        </div>
      </div>`;
    }).join('');
  }

  /* ──────────────────────────── year series ──────────────────────── */

  function drawYears(set: Agreement[]): void {
    zoom?.destroy();
    zoom = null;
    elYear.innerHTML = '';

    const subsetByYear = new Map<number, number>();
    for (const a of set) {
      const y = yearOf(a.registeredOn);
      if (y != null) subsetByYear.set(y, (subsetByYear.get(y) ?? 0) + 1);
    }

    const series = file.perYear;
    if (!series.length) {
      elYear.innerHTML = '<p class="xsmall muted">The register carries no registration dates to plot.</p>';
      return;
    }
    const tk = ticks(Math.max(1, ...series.map((p) => p.count)), 4);
    const top = tk[tk.length - 1];

    const W = chartWidth();
    lastW = W;
    const H = 230;
    const ml = 44;
    const mr = 12;
    const mt = 16;
    const mb = 34;
    const iw = W - ml - mr;
    const ih = H - mt - mb;
    const bw = iw / series.length;
    const labelStep = Math.max(1, Math.ceil(34 / bw));
    const y = (v: number) => mt + ih - (v / top) * ih;

    const svg = chart(W, H, `Agreements entered on the register each year, ${series[0].year} to ${series[series.length - 1].year}`);
    const selColour = state.top ? colourOf(state.top) : INK;

    for (const t of tk) {
      svg.append(el('line', { class: 'grid-line', x1: ml, x2: ml + iw, y1: y(t), y2: y(t) }));
      svg.append(el('text', { class: 'axis-text', x: ml - 6, y: y(t) + 4, 'text-anchor': 'end' }, num(t)));
    }
    svg.append(el('line', { class: 'axis-line', x1: ml, x2: ml + iw, y1: mt + ih, y2: mt + ih }));
    svg.append(el('text', { class: 'axis-title', x: ml, y: mt - 4 }, 'agreements registered'));

    series.forEach((p, i) => {
      const x = ml + i * bw;
      const sub = subsetByYear.get(p.year) ?? 0;
      const partial = lastYear != null && p.year === lastYear;
      const g = el('g');
      g.append(el('rect', {
        x: x + bw * 0.14, width: Math.max(1, bw * 0.72), y: y(p.count), height: Math.max(0.6, mt + ih - y(p.count)),
        fill: '#e3dccd', stroke: '#c2b9a4', 'stroke-width': 0.8,
        'stroke-dasharray': partial ? '2 2' : null,
      }));
      if (sub > 0) {
        g.append(el('rect', {
          x: x + bw * 0.3, width: Math.max(1, bw * 0.4), y: y(sub), height: Math.max(0.8, mt + ih - y(sub)),
          fill: selColour,
        }));
      }
      const hit = el('rect', { x, width: bw, y: mt, height: ih, fill: 'transparent', class: 'mark' });
      hit.setAttribute('data-tip', tip(
        `${p.year}${partial ? ' — part year' : ''}`,
        `${plural(p.count, 'agreement')} entered on the register`,
        `Current selection: ${plural(sub, 'agreement')}`,
        partial && extracted ? `The register was extracted ${longDate(extracted)}, so this year is incomplete.` : null,
      ));
      g.append(hit);
      svg.append(g);
      // Year labels thin out with the column width rather than overprinting
      // each other at 375px, and the part year is always labelled.
      if (i % labelStep === 0 || partial) {
        svg.append(el('text', { class: 'axis-text', x: x + bw / 2, y: H - 14, 'text-anchor': 'middle' }, String(p.year)));
      }
    });

    elYear.append(svg);
    // The SVG must be inside its `.chart-frame` parent before this call, or the
    // zoom controls are appended to nothing and silently never appear. The
    // guard is for jsdom, which does not implement `viewBox.baseVal` at all —
    // in a browser it is always there, so no real reader loses the controls.
    if (svg.viewBox?.baseVal) zoom = attachSvgZoom(svg, { maxScale: 6 });

    elYearSub.innerHTML = `Pale bars are all ${esc(num(TOTAL))} agreements, by the year they went on the register.
      The narrow solid bar inside each is the current selection — <strong>${esc(selectionLabel())}</strong>.`;
    elYearFoot.textContent = `${lastYear ?? ''} is a part year — the register was extracted ${longDate(extracted)} `
      + 'and its bar is drawn with a dashed edge. Two agreements are still in notification and carry no '
      + 'registration date, so they appear in no bar at all.';
  }

  /* ───────────────────────────── the list ────────────────────────── */

  function describeFilters(): string {
    const bits: string[] = [];
    if (state.top) bits.push(`subject: ${selectionLabel()}`);
    if (state.juris) bits.push(`jurisdiction: ${JURIS_NAME[state.juris] ?? state.juris}`);
    if (state.flag) bits.push(`${FLAG_META[state.flag.key].label} — ${FLAG_STATE_LABEL[state.flag.state]}`);
    return bits.length ? `Filters: ${bits.join(' · ')}.` : '';
  }

  function drawList(set: Agreement[]): void {
    listed = [...set].sort((a, b) => {
      const ka = a.registeredOn ?? a.notified ?? a.lodged ?? '';
      const kb = b.registeredOn ?? b.notified ?? b.lodged ?? '';
      return kb.localeCompare(ka);
    });

    if (!listed.length) {
      elShowAll.hidden = true;
      elListSub.textContent = 'Nothing matches the current filter.';
      elList.innerHTML = `<div class="empty-state">
        <p><strong>The register records no agreement matching this selection.</strong></p>
        <p class="small">${esc(describeFilters() || 'No filter is active.')}</p>
        <p class="small">That is what the register says, not a gap in this page.</p>
        <button type="button" class="btn btn-sm" data-act="clear" style="margin-top:var(--space-md)">Clear the filters</button>
      </div>`;
      return;
    }

    const seaCount = listed.filter((a) => a.sea).length;
    const shown = state.showAll ? listed : listed.slice(0, LIST_CAP);
    elShowAll.hidden = listed.length <= LIST_CAP;
    elShowAll.textContent = state.showAll ? `List only the first ${num(LIST_CAP)}` : `List all ${num(listed.length)}`;
    elShowAll.setAttribute('aria-pressed', String(state.showAll));

    elListSub.innerHTML = `${esc(plural(listed.length, 'agreement'))} match <strong>${esc(selectionLabel())}</strong>${
      state.juris ? ` in ${esc(JURIS_NAME[state.juris] ?? state.juris)}` : ''}${
      state.flag ? `, with ${esc(FLAG_META[state.flag.key].label)} ${esc(FLAG_STATE_LABEL[state.flag.state])}` : ''}.
      ${seaCount ? `${esc(plural(seaCount, 'of them covers sea country', 'of them cover sea country'))}. ` : ''}No area
      total is given for a selection: this register's area column double-counts every piece of ground two
      agreements share, so adding it up would state something untrue. Only the dissolved union in the note
      above is an area.
      Newest first${listed.length > shown.length ? `; the first ${esc(num(shown.length))} are drawn below` : ''}.`;

    elList.innerHTML = `<div class="table-scroll"><table>
      <thead><tr>
        <th>Agreement</th><th>Status</th><th>Registered</th><th>Jurisdiction</th>
        <th>Type</th><th>Authorisation</th><th>Representative</th><th>Subject matter (SUBJMATTER)</th>
      </tr></thead>
      <tbody>${shown.map((a, i) => `<tr>
        <td><button type="button" class="agr-name-btn verbatim" data-row="${i}"
          data-tip="${tipAttr(a.name ?? a.id, 'Choose for the full record of this agreement.')}">${esc(a.name ?? a.id)}</button></td>
        <td class="small">${esc(a.status ?? 'Not recorded')}</td>
        <td class="small mono nowrap">${esc(a.registeredOn ? shortDate(a.registeredOn) : 'Not yet registered')}</td>
        <td class="small nowrap">${esc(a.juris)}</td>
        <td class="small">${esc(a.agreementType ?? 'Not recorded')}</td>
        <td class="small">${a.certified ? 'Certified' : '<span class="muted">Not certified</span>'}</td>
        <td class="small">${a.representative ? `<span class="verbatim">${esc(a.representative)}</span>` : '<span class="muted">Not recorded</span>'}</td>
        <td class="small"><span class="verbatim">${esc(a.subjectText ?? 'Not recorded')}</span></td>
      </tr>`).join('')}</tbody>
    </table></div>`;
  }

  /* ─────────────────────── jurisdiction & chips ──────────────────── */

  function drawJuris(): void {
    elJuris.innerHTML = '<span class="xsmall muted shrink0">Jurisdiction:</span>' + JURIS_ORDER.map((code) => {
      const c = jurisCounts.get(code) ?? 0;
      const on = state.juris === code;
      return `<button type="button" class="pill" data-juris="${code}" aria-pressed="${on}"
        aria-label="${esc(JURIS_NAME[code])}, ${esc(plural(c, 'agreement'))}"
        data-tip="${tipAttr(
          JURIS_NAME[code],
          c === 0
            ? 'The register records no agreement here at all. A real zero, not missing data.'
            : `${plural(c, 'agreement')} — ${pct((c / TOTAL) * 100)} of the ${num(TOTAL)} on the register`,
          on ? 'Chosen. Choose again to clear.' : 'Choose to keep only these agreements.',
        )}">${esc(code)} <span class="mono">${esc(num(c))}</span></button>`;
    }).join('');
  }

  function drawChips(set: Agreement[]): void {
    const bits: string[] = [];
    if (state.top) {
      bits.push(`<button type="button" class="pill" data-act="clear-subject" data-tip="${tipAttr('Clear the subject filter')}"
        ><span class="legend-swatch" style="background:${esc(colourOf(state.top))}"></span>${esc(selectionLabel())} ✕</button>`);
    }
    if (state.juris) {
      bits.push(`<button type="button" class="pill" data-act="clear-juris" data-tip="${tipAttr('Clear the jurisdiction filter')}"
        >${esc(JURIS_NAME[state.juris] ?? state.juris)} ✕</button>`);
    }
    if (state.flag) {
      bits.push(`<button type="button" class="pill" data-act="clear-flag" data-tip="${tipAttr('Clear the statutory-flag filter')}"
        >${esc(FLAG_META[state.flag.key].label)}: ${esc(FLAG_STATE_LABEL[state.flag.state])} ✕</button>`);
    }
    elChips.innerHTML = bits.length
      ? `<span class="xsmall muted shrink0">Filtering:</span>${bits.join('')}
         <button type="button" class="btn btn-sm" data-act="clear">Clear all</button>
         <span class="xsmall muted">${esc(plural(set.length, 'agreement'))} of ${esc(num(TOTAL))}</span>`
      : `<span class="xsmall muted">No filter is active — all ${esc(num(TOTAL))} agreements are in view.</span>`;
  }

  /* ────────────────────────────── redraw ─────────────────────────── */

  function apply(): void {
    const pool = poolBeforeSubject();
    const set = applySubject(pool);
    drawJuris();
    drawChips(set);
    drawIcicle(pool);
    drawRibbon(set);
    drawYears(set);
    drawList(set);
    syncUrl();
  }

  /* ──────────────────────────── interaction ──────────────────────── */

  function clearSubject(): void {
    state.top = null;
    state.sub = null;
    state.noSub = false;
    state.bundle = false;
    state.subBundle = false;
  }

  elWrap.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest('[data-act="subback"]')) { state.subBundle = false; apply(); return; }
    const btn = target.closest<HTMLElement>('.agr-band');
    if (!btn) return;
    const band = (elRowTop.contains(btn) ? topBands : subBands)[Number(btn.dataset.band)];
    if (!band) return;
    state.showAll = false;

    switch (band.act) {
      case 'bundle':
        state.bundle = !state.bundle;
        if (state.bundle) { state.sub = null; state.noSub = false; state.subBundle = false; }
        break;
      case 'top':
        if (state.top === band.key && !state.sub && !state.noSub && !state.bundle) clearSubject();
        else if (state.top === band.key && !state.bundle) { state.sub = null; state.noSub = false; state.subBundle = false; }
        else { state.top = band.key; state.sub = null; state.noSub = false; state.subBundle = false; state.bundle = false; }
        break;
      case 'subbundle':
        state.subBundle = true;
        state.sub = null;
        state.noSub = false;
        break;
      case 'nosub':
        state.noSub = !state.noSub;
        state.sub = null;
        break;
      default:
        if (state.sub === band.key) state.sub = null;
        else { state.sub = band.key; state.noSub = false; }
    }
    apply();
  });

  elRibbon.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-flag]');
    if (!btn) return;
    const key = btn.dataset.flag ?? '';
    const value = btn.dataset.state ?? '';
    if (!(FLAG_KEYS as readonly string[]).includes(key) || !(FLAG_STATES as string[]).includes(value)) return;
    const next = { key: key as FlagKey, state: value as FlagState };
    state.flag = state.flag && state.flag.key === next.key && state.flag.state === next.state ? null : next;
    state.showAll = false;
    apply();
  });

  elJuris.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-juris]');
    if (!btn) return;
    const code = btn.dataset.juris ?? '';
    state.juris = state.juris === code ? null : code;
    state.showAll = false;
    apply();
  });

  elChips.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-act]');
    if (!btn) return;
    switch (btn.dataset.act) {
      case 'clear-subject': clearSubject(); break;
      case 'clear-juris': state.juris = null; break;
      case 'clear-flag': state.flag = null; break;
      case 'clear': clearSubject(); state.juris = null; state.flag = null; break;
      default: return;
    }
    state.showAll = false;
    apply();
  });

  elList.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest('[data-act="clear"]')) {
      clearSubject();
      state.juris = null;
      state.flag = null;
      state.showAll = false;
      apply();
      return;
    }
    const btn = target.closest<HTMLElement>('[data-row]');
    if (!btn) return;
    const a = listed[Number(btn.dataset.row)];
    if (a) openAgreement(a);
  });

  elShowAll.addEventListener('click', () => {
    state.showAll = !state.showAll;
    drawList(applySubject(poolBeforeSubject()));
  });

  apply();

  const remeasure = (): void => {
    if (!elYear.isConnected || Math.abs(chartWidth() - lastW) < 9) return;
    drawYears(applySubject(poolBeforeSubject()));
  };
  setTimeout(remeasure, 0);
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(remeasure).observe(elYear);

  // A deep link to one agreement reopens its record, so a shared URL lands
  // where the sender was rather than merely near it.
  const deep = currentRoute().params.get('agr');
  if (deep) {
    const found = rows.find((a) => a.id === deep);
    if (found) openAgreement(found);
  }
}

/* ─────────────────────── one agreement, in full ────────────────────────── */

function flagLine(a: Agreement, k: FlagKey): string {
  const s = flagStateOf(a, k);
  const swatch = s === 'unrecorded'
    ? '<span class="legend-swatch agr-sw-unrec"></span>'
    : `<span class="legend-swatch" style="background:${s === 'yes' ? INK : INK_PALE}"></span>`;
  const value = s === 'unrecorded' ? 'Not recorded — the field is blank' : s === 'yes' ? 'Yes' : 'No';
  return `<div class="detail-field">
    <div class="detail-label">${esc(FLAG_META[k].label)} <span class="mono">${esc(FLAG_META[k].column)}</span></div>
    <div class="row" style="gap:var(--space-xs)">${swatch}<span>${esc(value)}</span></div>
    <div class="xsmall muted">${esc(FLAG_META[k].plain)}</div>
  </div>`;
}

function openAgreement(a: Agreement): void {
  openOverlay({
    panelClass: 'drawer',
    label: `Agreement: ${a.name ?? a.id}`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.className = 'verbatim';
      h.textContent = a.name ?? a.id;
      head.append(h, closeButton(close, 'Close this agreement'));

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `
        <div class="detail-headline">
          <span class="pill">${esc(a.status ?? 'Status not recorded')}</span>
          ${a.agreementType ? `<span class="pill">${esc(a.agreementType)}</span>` : ''}
          <span class="pill">${a.certified ? 'Certified' : 'Not certified'}</span>
          ${a.sea ? '<span class="pill">Includes sea country</span>' : ''}
        </div>
        <p class="small muted">An ${glossTerm('ilua', 'Indigenous Land Use Agreement')} is a voluntary, legally
          binding agreement about how land and waters will be used. Once registered it binds everyone who holds
          native title in the area — including people who were not party to the negotiation.</p>

        <div class="detail-block">
          <h3>Subject matter, as the register states it</h3>
          <p class="xsmall muted">The SUBJMATTER field, verbatim. It is an index term, not a description of
            what the agreement contains.</p>
          <div class="verbatim holders-text">${esc(a.subjectText ?? 'Not recorded')}</div>
        </div>

        <div class="detail-block">
          <h3>What the register records it as doing</h3>
          <p class="xsmall muted">Five yes/no fields. A blank is not a no — it is drawn and counted as its own
            third state everywhere on this site.</p>
          <div class="detail-grid">${FLAG_KEYS.map((k) => flagLine(a, k)).join('')}</div>
        </div>

        <div class="detail-block">
          <h3>The record</h3>
          <div class="detail-grid">
            <div class="detail-field"><div class="detail-label">Tribunal reference</div><div class="mono">${esc(a.id)}</div></div>
            <div class="detail-field"><div class="detail-label">Jurisdiction</div><div>${esc(JURIS_NAME[a.juris] ?? a.juris)}</div></div>
            <div class="detail-field"><div class="detail-label">Lodged</div><div>${esc(longDate(a.lodged))}</div></div>
            <div class="detail-field"><div class="detail-label">Notification</div><div>${esc(longDate(a.notified))}</div></div>
            <div class="detail-field"><div class="detail-label">Entered on the register</div><div>${esc(a.registeredOn ? longDate(a.registeredOn) : 'Not yet registered')}</div></div>
            <div class="detail-field"><div class="detail-label">Agreement runs from</div><div>${esc(longDate(a.start))}</div></div>
            <div class="detail-field"><div class="detail-label">Agreement runs to</div><div>${esc(a.end ? longDate(a.end) : 'No end date recorded')}</div></div>
            <div class="detail-field"><div class="detail-label">Representative</div><div class="verbatim">${esc(a.representative ?? 'Not recorded')}</div></div>
          </div>
        </div>

        <div class="detail-block">
          <h3>Area</h3>
          <p class="mono">${esc(km2(a.areaSqKm, { exact: true }))}</p>
          <p class="xsmall muted">This is the register's own area figure for this agreement. Agreements overlap
            one another, so these figures cannot be added together: summed across the register they come to more
            than twice the ground actually covered.</p>
        </div>

        <div class="note legal xsmall">The boundaries behind these figures are indicative only. They are not
          cadastral or legal boundaries, they cannot be used to establish rights over any particular parcel of
          land or water, and nothing here is legal advice. The Tribunal's own register of agreements is the
          authoritative record.</div>
      `;

      panel.append(head, body);
    },
    onClose() {
      if (currentRoute().view === 'agreements') setParams({ agr: null });
    },
  });
  if (currentRoute().view === 'agreements') setParams({ agr: a.id });
}
